/**
 * FanPanel.tsx — fan context column (Design Spec §07; D-10, D-11, D-13, D-17, ARCH §9.3).
 *
 * Always-on third column: identity & id · nickname · notes · tags ·
 * translation mode · destructive block/report (held in red at the bottom).
 * (Mute moved to the conversation header + handoff status removed — owner 2026-07-01.)
 *
 * CRITICAL (T-04-35 / D-13):
 *   - Shows totalSpendForCreator (per-fan spend — allowed per D-13)
 *   - NO aggregate revenue, NO KPI charts, NO dashboard financials
 *
 * All actions are server-audited (Plan 06). Times Europe/Berlin (D-09).
 */

import * as React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fansApi, ApiRequestError } from '../lib/api-client.js';
import { Button, Badge, Textarea, Input, useToast } from '@crm/ui';
import { FanAvatar } from './FanAvatar.js';
import { formatEurMinor, fanDisplayName } from '../lib/format.js';

/**
 * Fetches a fan's recent per-transaction payments (Recent payments section). Chatter-only
 * data (transactionsApi Surface B) is NOT pulled into @crm/chat (Phase 12-02 design note) —
 * this is supplied as an injected slot by the chatter app's ChatterWorkspace wrapper.
 */
export type FetchRecentPayments = (
  memberId: string,
  creatorId: string,
) => Promise<{ data: Array<{ id: string; kind: string; payoutMinor: number | null }> } | null>;

interface FanPanelProps {
  conversationId: string;
  accountId: string;
  /** Maloum member ID (fanId from conversation row) */
  memberId: string | null;
  maloumChatId: string;
  /**
   * Creator ID — required to scope the recent-payments query (D-07-12).
   * When absent the recent-payments section is hidden.
   */
  creatorId?: string | null;
  /** Chatter-only: fetch this fan's recent payments. Omit to hide the section. */
  fetchRecentPayments?: FetchRecentPayments;
  /**
   * Admin read-only, all-accounts monitoring mode (Phase 12-03, D-02). Hides the Actions
   * section (block/unblock/report) and the notes/nickname/tags "Save" controls. The
   * per-fan spend/whale badge (D-05) and every read-only section stay visible — admins
   * are un-redacted. Omitted/false for the chatter surface (byte-identical).
   */
  readOnly?: boolean;
}

export function FanPanel({
  conversationId,
  accountId,
  memberId,
  maloumChatId,
  creatorId,
  fetchRecentPayments,
  readOnly = false,
}: FanPanelProps) {
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  const fanQuery = useQuery({
    queryKey: ['fan', memberId, accountId],
    queryFn: () => (memberId ? fansApi.get(memberId, accountId) : Promise.resolve(null)),
    enabled: !!memberId,
  });

  // Recent payments for this fan — per D-07-12. Scoped to creatorId (server-enforced).
  // NO aggregate totals — per-transaction amounts only (D-07-13 hard boundary).
  const recentTxQuery = useQuery({
    queryKey: ['chatter-transactions', 'fan', memberId, creatorId],
    queryFn: () =>
      memberId && creatorId && fetchRecentPayments
        ? fetchRecentPayments(memberId, creatorId)
        : Promise.resolve(null),
    enabled: !!memberId && !!creatorId && !!fetchRecentPayments,
    staleTime: 60_000,
  });

  const fan = fanQuery.data;

  // Local edit state
  const [notes, setNotes] = React.useState('');
  const [nickname, setNickname] = React.useState('');
  const [tagInput, setTagInput] = React.useState('');
  const [tags, setTags] = React.useState<string[]>([]);

  React.useEffect(() => {
    if (fan) {
      setNotes(fan.notes ?? '');
      setNickname(fan.nickname ?? '');
      setTags(fan.tags ?? []);
    }
  }, [fan]);

  // Mutations
  function onError(action: string, err: unknown) {
    const msg =
      err instanceof ApiRequestError
        ? typeof err.body.message === 'string'
          ? err.body.message
          : err.body.message.join('; ')
        : (err as Error).message ?? `${action} failed`;
    addToast(`${action} failed: ${msg}`, 'danger');
  }

  const notesMut = useMutation({
    mutationFn: () =>
      memberId
        ? fansApi.updateNotes(memberId, accountId, maloumChatId, notes)
        : Promise.resolve(undefined as void),
    onSuccess: () => {
      addToast('Notes saved', 'success');
      queryClient.invalidateQueries({ queryKey: ['fan', memberId, accountId] });
    },
    onError: (err) => onError('Save notes', err),
  });

  const nicknameMut = useMutation({
    mutationFn: () =>
      memberId
        ? fansApi.updateNickname(memberId, accountId, maloumChatId, nickname)
        : Promise.resolve(undefined as void),
    onSuccess: () => {
      addToast('Nickname saved', 'success');
      queryClient.invalidateQueries({ queryKey: ['fan', memberId, accountId] });
    },
    onError: (err) => onError('Save nickname', err),
  });

  const tagsMut = useMutation({
    mutationFn: () =>
      memberId
        ? fansApi.updateTags(memberId, accountId, maloumChatId, tags)
        : Promise.resolve(undefined as void),
    onSuccess: () => {
      addToast('Tags saved', 'success');
      queryClient.invalidateQueries({ queryKey: ['fan', memberId, accountId] });
    },
    onError: (err) => onError('Save tags', err),
  });

  const blockMut = useMutation({
    mutationFn: () => (memberId ? fansApi.block(memberId, accountId) : Promise.resolve(undefined as void)),
    onSuccess: () => {
      addToast('Fan blocked', 'success');
      queryClient.invalidateQueries({ queryKey: ['fan', memberId, accountId] });
    },
    onError: (err) => onError('Block fan', err),
  });

  const unblockMut = useMutation({
    mutationFn: () => (memberId ? fansApi.unblock(memberId, accountId) : Promise.resolve(undefined as void)),
    onSuccess: () => {
      addToast('Fan unblocked', 'success');
      queryClient.invalidateQueries({ queryKey: ['fan', memberId, accountId] });
    },
    onError: (err) => onError('Unblock fan', err),
  });

  const reportMut = useMutation({
    mutationFn: () => (memberId ? fansApi.report(memberId, accountId) : Promise.resolve(undefined as void)),
    onSuccess: () => {
      addToast('Fan reported', 'success');
      queryClient.invalidateQueries({ queryKey: ['fan', memberId, accountId] });
    },
    onError: (err) => onError('Report fan', err),
  });

  // Per-fan auto-translate mode (migration 0014). Global for this creator+fan. On success we
  // also refresh the inbox list (so the conversation's autoTranslate flag updates the header +
  // composer) and the message thread (so it re-renders translated ⇄ original).
  const autoTranslateMut = useMutation({
    mutationFn: (enabled: boolean) =>
      memberId ? fansApi.setAutoTranslate(memberId, accountId, enabled) : Promise.resolve(undefined as void),
    onSuccess: (_, enabled) => {
      addToast(enabled ? 'Auto-translation on (German mode)' : 'Auto-translation off (English mode)', 'success');
      queryClient.invalidateQueries({ queryKey: ['fan', memberId, accountId] });
      queryClient.invalidateQueries({ queryKey: ['inbox'] });
      queryClient.invalidateQueries({ queryKey: ['messages', conversationId] });
    },
    onError: (err) => onError('Update translation mode', err),
  });

  function addTag() {
    const t = tagInput.trim();
    if (t && !tags.includes(t)) setTags((prev) => [...prev, t]);
    setTagInput('');
  }
  function removeTag(tag: string) {
    setTags((prev) => prev.filter((t) => t !== tag));
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (!memberId) {
    return <div className="p-4 text-xs text-text-muted">Fan profile not yet reconciled.</div>;
  }
  if (fanQuery.isLoading) {
    return <div className="p-4 text-xs text-text-muted">Loading fan profile…</div>;
  }

  const displayName = fanDisplayName({
    nickname: fan?.nickname,
    username: fan?.username,
    fanId: memberId,
  });

  return (
    <div className="flex flex-col h-full">
      {/* Identity */}
      <div className="flex flex-col items-center gap-1.5 px-4 pt-4 pb-3 border-b border-border text-center">
        <FanAvatar nickname={fan?.nickname} username={fan?.username} fanId={memberId} size="lg" />
        <div className="flex flex-col items-center gap-0.5 min-w-0 w-full">
          <span className="text-sm font-semibold text-text truncate max-w-full">{displayName}</span>
          {/* Username under a nickname title — never the raw member id (owner 2026-07-02). */}
          {fan?.username && fan.username !== displayName && (
            <span className="text-[11px] text-text-subtle truncate max-w-full">@{fan.username}</span>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap justify-center">
          {fan?.totalSpendForCreator != null && fan.totalSpendForCreator > 0 && (
            <span className="text-xs text-success font-mono">
              {formatEurMinor(fan.totalSpendForCreator)} spent
            </span>
          )}
          {fan?.isBlocked && <Badge variant="danger">Blocked</Badge>}
          {fan?.isReported && <Badge variant="warning">Reported</Badge>}
          {fan?.isMuted && <Badge variant="default">Muted</Badge>}
          {fan && !fan.autoTranslate && <Badge variant="info">English</Badge>}
        </div>
        {fanQuery.isError && (
          <p className="text-[11px] text-warning" role="status">
            Saved profile couldn’t load — edits below still save.
          </p>
        )}
      </div>

      <div className="flex-1 overflow-y-auto flex flex-col">
        {/* Nickname */}
        <Section title="Nickname">
          <Input
            label=""
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            placeholder="Optional nickname"
            disabled={readOnly}
          />
          {!readOnly && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => nicknameMut.mutate()}
              loading={nicknameMut.isPending}
              className="self-stretch justify-center"
            >
              Save nickname
            </Button>
          )}
        </Section>

        {/* Notes (D-10) */}
        <Section title="Notes">
          <Textarea
            label=""
            aria-label="Notes about this fan"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes about this fan…"
            rows={4}
            disabled={readOnly}
          />
          {!readOnly && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => notesMut.mutate()}
              loading={notesMut.isPending}
              className="self-stretch justify-center"
            >
              Save notes
            </Button>
          )}
        </Section>

        {/* Tags (D-10 — free-form) */}
        <Section title="Tags">
          <div className="flex flex-wrap gap-1.5">
            {tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-bg-hover border border-border rounded-full text-text"
              >
                {tag}
                {!readOnly && (
                  <button
                    onClick={() => removeTag(tag)}
                    className="text-text-subtle hover:text-danger transition-colors leading-none"
                    aria-label={`Remove tag ${tag}`}
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
            {tags.length === 0 && <span className="text-xs text-text-subtle">No tags</span>}
          </div>
          {!readOnly && (
            <div className="flex gap-2">
              <Input
                label=""
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addTag();
                  }
                }}
                placeholder="Add tag…"
              />
              <Button size="sm" variant="outline" onClick={addTag} disabled={!tagInput.trim()} className="shrink-0">
                Add
              </Button>
            </div>
          )}
          {!readOnly && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => tagsMut.mutate()}
              loading={tagsMut.isPending}
              className="self-stretch justify-center"
            >
              Save tags
            </Button>
          )}
        </Section>

        {/* Translation mode — per-fan auto-translate (migration 0014; global for this creator+fan).
            The current mode stays VISIBLE (admins monitor it), but the toggle Button is a real,
            audited, global-for-this-creator+fan write — gate it behind !readOnly like every other
            write control in this panel (Phase 12 CR-01 / D-02: read-only admins must not mutate). */}
        <Section
          title="Translation"
          aside={
            <span className="text-[10px] font-mono uppercase tracking-wide text-text-subtle">
              global
            </span>
          }
        >
          {!readOnly && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => autoTranslateMut.mutate(!(fan?.autoTranslate ?? true))}
              loading={autoTranslateMut.isPending}
              className="self-stretch justify-center"
            >
              {(fan?.autoTranslate ?? true)
                ? 'Switch to English (no translation)'
                : 'Switch to German (auto-translate)'}
            </Button>
          )}
          <p className="text-[11px] text-text-subtle">
            {(fan?.autoTranslate ?? true)
              ? 'German mode: incoming is translated to English and your replies are sent as German.'
              : 'English mode: messages show as-is and your replies send in English (no translation). Applies for every chatter on this creator.'}
          </p>
        </Section>

        {/* Recent payments — per D-07-12 (per-tx only; NO aggregates per D-07-13) */}
        {creatorId && fetchRecentPayments && (
          <Section title="Recent payments">
            {recentTxQuery.isLoading ? (
              <span className="text-xs text-text-muted">Loading…</span>
            ) : recentTxQuery.isError ? (
              <span className="text-xs text-warning">Could not load payments.</span>
            ) : !recentTxQuery.data?.data.length ? (
              <span className="text-xs text-text-subtle italic">No payments yet.</span>
            ) : (
              <div className="flex flex-col gap-1">
                {recentTxQuery.data.data.slice(0, 5).map((tx) => (
                  <div key={tx.id} className="flex items-center justify-between py-0.5 text-xs">
                    <span className="text-text-muted capitalize">{tx.kind.replace('_', ' ')}</span>
                    <span className="font-mono font-semibold text-text">
                      {/* Owner 2026-07-04: show net = creator payout (amount_payout_minor),
                          never the VAT+fee-inclusive gross. */}
                      {tx.payoutMinor != null ? formatEurMinor(tx.payoutMinor, 2) : '—'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Section>
        )}

        {/* Actions (D-17 — server-audited; held in red at the bottom). Hidden entirely in
            readOnly (D-02) — an admin viewer never blocks/unblocks/reports a fan. */}
        {!readOnly && (
          <Section title="Actions" last>
            {fan?.isBlocked ? (
              <DangerButton onClick={() => unblockMut.mutate()} loading={unblockMut.isPending} variant="hollow">
                Unblock fan
              </DangerButton>
            ) : (
              <DangerButton
                onClick={() => {
                  if (confirm('Block this fan? This will be server-audited.')) blockMut.mutate();
                }}
                loading={blockMut.isPending}
              >
                Block fan
              </DangerButton>
            )}
            <DangerButton
              onClick={() => {
                if (confirm('Report this fan? This will be server-audited.')) reportMut.mutate();
              }}
              loading={reportMut.isPending}
              disabled={fan?.isReported}
            >
              {fan?.isReported ? 'Already reported' : 'Report fan'}
            </DangerButton>
          </Section>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section + DangerButton
// ---------------------------------------------------------------------------

function Section({
  title,
  aside,
  children,
  last,
}: {
  title: string;
  aside?: React.ReactNode;
  children: React.ReactNode;
  last?: boolean;
}) {
  return (
    <section className={['flex flex-col gap-1.5 px-4 py-3', last ? '' : 'border-b border-border'].join(' ')}>
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-text-subtle">{title}</h3>
        {aside}
      </div>
      {children}
    </section>
  );
}

function DangerButton({
  children,
  onClick,
  loading,
  disabled,
  variant = 'hollow',
}: {
  children: React.ReactNode;
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
  variant?: 'hollow';
}) {
  const isDisabled = disabled || loading;
  return (
    <button
      onClick={onClick}
      disabled={isDisabled}
      className={[
        'inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md border transition-colors',
        'border-danger/50 text-danger hover:bg-danger/10',
        isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
      ].join(' ')}
    >
      {loading && (
        <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
        </svg>
      )}
      {children}
    </button>
  );
}
